import { z } from 'zod/v4'
import {
  CountryCode,
  getCountries,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import type { Language } from '@skop-app/lib-domain/types'

import { type Translator } from '@/types'

export const stringValidation = (t: Translator) =>
  z.string({
    error: t('ZodErrorMessages.mandatory'),
  })

export const reactSelectValidation = (t: Translator) =>
  z.object(
    {
      label: z.string(),
      value: z.string(),
    },
    {
      error: t('ZodErrorMessages.select'),
    },
  )

export const siretValidation = (t: Translator) =>
  z
    .string({
      error: t('ZodErrorMessages.mandatory'),
    })
    .refine(
      (value) => {
        const siretPattern = /^\d{3}\s*\d{3}\s*\d{3}\s*\d{5}$/
        return siretPattern.test(value)
      },
      {
        message: t('ZodErrorMessages.siret'),
      },
    )

export const vatValidation = (t: Translator) =>
  z
    .string({
      error: t('ZodErrorMessages.mandatory'),
    })
    .refine(
      (value) => {
        if (!value) return true
        const vatPattern =
          /^(?:FR\s*\d{2}\s*\d{3}\s*\d{3}\s*\d{3}|CHE[-\s]?\d{3}[.\s]?\d{3}[.\s]?\d{3}(?:\s*(?:TVA|MWST|IVA))?)$/i
        return vatPattern.test(value)
      },
      {
        message: t('ZodErrorMessages.vat'),
      },
    )

/**
 * @param lang - The language of the phone number
 * @param t - The translation function
 * @param isNormalized - Whether the phone number is normalized
 * @returns A Zod schema for the phone number
 */
export const phoneValidation = (
  lang: Language,
  t: Translator,
  isNormalized = false,
  country?: string | null,
) => {
  if (isNormalized) {
    const normalizedCountry = normalizeCountry(country)
    const phoneRegion = isCountryCode(normalizedCountry)
      ? (normalizedCountry as CountryCode)
      : String(lang).toUpperCase() === 'NL'
        ? 'NL'
        : 'FR'
    return z
      .string({
        error: t('ZodErrorMessages.mandatory'),
      })
      .refine((value) => !value || isValidPhoneNumber(value, phoneRegion), {
        message: t('ZodErrorMessages.phone'),
      })
      .nullable()
      .or(z.literal(''))
  } else {
    return z
      .string({
        error: t('ZodErrorMessages.mandatory'),
      })
      .refine(
        (value) => {
          if (!value) return true
          try {
            return isValidPhoneNumber(value, lang as CountryCode)
          } catch {
            return false
          }
        },
        { message: t('ZodErrorMessages.phone') },
      )
  }
}

export const numberStringValidation = z.preprocess(
  (val) => (typeof val === 'string' ? parseFloat(val) : val),
  z.number().min(0),
)

export const reactSelectNumberValidation = z.object({
  label: z.string(),
  value: numberStringValidation,
})

export const optionalDateInputValidation = z
  .union([z.string(), z.date()])
  .nullable()
  .optional()

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function getSeparators(locale: string) {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
  const group = parts.find((p) => p.type === 'group')?.value ?? ''
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.'
  return { group, decimal }
}

export function parseLocaleNumber(str: string, locale: string) {
  const { group, decimal } = getSeparators(locale)
  const normalized = str
    .replace(new RegExp(escapeRegExp(group), 'g'), '')
    .replace(new RegExp(escapeRegExp(decimal), 'g'), '.')
  return parseFloat(normalized)
}

export const looseLocaleNumberValidation = (locale: string) =>
  z.preprocess((val) => {
    if (val == null) return undefined
    if (typeof val === 'number') return val
    if (typeof val !== 'string') return undefined
    const trimmed = val.trim()
    if (!trimmed) return undefined
    const parsed = parseLocaleNumber(trimmed, locale)
    return isNaN(parsed) ? undefined : parsed
  }, z.number().min(0).nullish())

/** Required ≥0 number; accepts Intl-formatted strings (e.g. "1 228,15") for the given locale. */
export const localeDecimalStringValidation = (locale: string) =>
  z.preprocess((val) => {
    if (val == null) return undefined
    if (typeof val === 'number') return val
    if (typeof val !== 'string') return undefined
    const trimmed = val.trim()
    if (!trimmed) return undefined
    const parsed = parseLocaleNumber(trimmed, locale)
    return Number.isNaN(parsed) ? undefined : parsed
  }, z.number().min(0))

/**
 * Optional price field: empty/null allowed; when present must be a finite number >= 0.
 * Prevents NaN when value is used as Number(price) elsewhere.
 */
export const priceValidation = (t: Translator) =>
  z.optional(
    z
      .string()
      .nullable()
      .or(z.literal(''))
      .refine(
        (val) => {
          if (val == null || val === '') return true
          const n = parseFloat(String(val).trim())
          return !Number.isNaN(n) && Number.isFinite(n) && n >= 0
        },
        { message: t('ZodErrorMessages.price-invalid') },
      ),
  )

/**
 * Optional field: empty/null allowed; when present must be a finite number >= 0.
 * Accepts both hydrated numeric values and user-entered strings from form inputs.
 */
export const optionalNonNegativeNumberValidation = (t: Translator) =>
  z.preprocess(
    (val) => {
      if (val == null) return null

      if (typeof val === 'number') {
        return Number.isFinite(val) ? val : val
      }

      if (typeof val !== 'string') return val

      const trimmed = val.trim()
      if (!trimmed) return null

      const parsed = Number(trimmed.replace(',', '.'))
      return Number.isFinite(parsed) ? parsed : val
    },
    z
      .number({ error: t('ZodErrorMessages.price-invalid') })
      .min(0, { error: t('ZodErrorMessages.price-invalid') })
      .nullable()
      .optional(),
  )

export const isSwissCountry = (country?: string | null) =>
  normalizeCountry(country) === 'CH'

export const zipcodeValidation = (t: Translator, country?: string | null) => {
  const countryCode = normalizeCountry(country)
  const pattern = ['CH', 'BE', 'LU'].includes(countryCode)
    ? /^\d{4}$/
    : countryCode === 'NL'
      ? /^\d{4}\s?[A-Za-z]{2}$/
      : /^\d{5}$/
  return stringValidation(t).regex(pattern, {
    error: t('ZodErrorMessages.invalid'),
  })
}

export const countryField = (t: Translator) => ({
  name: 'country',
  label: t('Account.country'),
  placeholder: 'France / Suisse',
  type: 'text',
  className: 'col-span-12 md:col-span-6',
  validation: z.string().nullable().optional(),
})

export const normalizeCountry = (country?: string | null): string => {
  const aliases: Record<string, string> = {
    FRANCE: 'FR',
    FRA: 'FR',
    FRANKRIJK: 'FR',
    SUISSE: 'CH',
    CHE: 'CH',
    SWITZERLAND: 'CH',
    ZWITSERLAND: 'CH',
    SCHWEIZ: 'CH',
    SVIZZERA: 'CH',
    BELGIQUE: 'BE',
    BELGIUM: 'BE',
    BELGIË: 'BE',
    BEL: 'BE',
    LUXEMBOURG: 'LU',
    LUXEMBURG: 'LU',
    LUX: 'LU',
    'PAYS-BAS': 'NL',
    NETHERLANDS: 'NL',
    NEDERLAND: 'NL',
    NLD: 'NL',
  }
  const value = country?.normalize('NFC').trim() ?? ''
  return aliases[value.toUpperCase()] ?? value.toUpperCase()
}

export const isCountryCode = (country?: string | null) =>
  getCountries().includes(normalizeCountry(country) as CountryCode)
